import { createContext, useContext, useRef, useState, ReactNode, useCallback } from 'react';

interface ConfirmOptions {
  title?: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({ description: '' });
  const resolveRef = useRef<(value: boolean) => void>(() => {});

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = () => { setOpen(false); resolveRef.current(true); };
  const handleCancel  = () => { setOpen(false); resolveRef.current(false); };

  const variantBtn = options.variant === 'danger'
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : options.variant === 'warning'
    ? 'bg-[#EF9F27] hover:bg-[#ba7517] text-white'
    : 'bg-[#00665C] hover:bg-[#004d45] text-white';

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      {/* Overlay + Dialog — en flow normal pour éviter les problèmes avec les iframes */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={handleCancel}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Titre */}
            <div className="flex items-start gap-3">
              {options.variant === 'danger' && (
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-600">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </span>
              )}
              <div>
                {options.title && (
                  <h2 className="text-base font-semibold text-gray-900 mb-1">{options.title}</h2>
                )}
                <p className="text-sm text-gray-600">{options.description}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {options.cancelLabel ?? 'Annuler'}
              </button>
              <button
                onClick={handleConfirm}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${variantBtn}`}
              >
                {options.confirmLabel ?? 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx.confirm;
}
